import type { v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import type { RadioItem } from '@deque/cauldron-react/lib/components/RadioGroup';
import type { TFunction } from 'i18next';
import { productHasAccessLevelManagement } from '~/common/constants';
import type { FeatureFlag } from '~/common/contexts/featureFlags';
import {
  AccessTypes,
  type Enterprises,
  type ProductAccess
} from '~/common/utils/billing-client/client-v2';

export interface ChangeAccess {
  product: v2.Product;
  accessType: AccessTypes;
}

export type AccessChanges = { [key in ProductSlugs]?: ChangeAccess };

export interface UserChanges {
  newProducts: v2.Product[];
  removalProducts: v2.Product[];
  accessChanges: AccessChanges;
  isAdmin: boolean;
}

export type AccessValue = 'general' | 'admin' | 'view' | 'remove' | 'none';

export const getCurrentAccess = (
  changes: Partial<UserChanges>,
  productSlug: ProductSlugs,
  currentAccess?: ProductAccess
) => {
  if (changes.accessChanges && changes.accessChanges[productSlug]) {
    // This line won't compile without the extra coalescing operators.
    // This is a known issue in the typescript compiler: https://github.com/microsoft/TypeScript/issues/10530
    return (
      changes.accessChanges?.[productSlug]?.accessType || AccessTypes.GENERAL
    );
  }
  return (currentAccess?.access_type as AccessTypes) || AccessTypes.GENERAL;
};

export const getAccessString = (t: TFunction, access_type?: string) => {
  if (access_type === AccessTypes.ADMIN) {
    return t('Admin');
  }
  if (access_type === AccessTypes.VIEW) {
    return t('View');
  }
  return t('General Access');
};

export const getLicenseDescription = (
  license: Enterprises.License,
  selectedMembersWithLicense: number,
  selectedMembersWithoutLicense: number,
  remainingLicenses: number,
  accessValue: AccessValue,
  t: TFunction
) => {
  const licensesAvailable = license.total
    ? license.total - license.used - license.pending
    : 0;

  if (
    remainingLicenses >= 0 &&
    (accessValue === 'general' ||
      accessValue === 'admin' ||
      accessValue === 'view')
  ) {
    if (remainingLicenses === 1) {
      return t('1 license available.');
    }
    return t('{{remainingLicenses}} licenses available.', {
      remainingLicenses
    });
  } else if (accessValue === 'none') {
    if (licensesAvailable === 1 && remainingLicenses >= 0) {
      return t('1 license available.');
    }

    if (licensesAvailable > 1 && remainingLicenses >= 0) {
      return t('{{remainingLicenses}} licenses available.', {
        remainingLicenses: licensesAvailable
      });
    }

    if (remainingLicenses < 0) {
      const licensesUsed = license.used + license.pending;

      if (licensesUsed === 1) {
        if (selectedMembersWithoutLicense === 1) {
          return t(
            '1 license used. 1 user without a license selected (1 over capacity).'
          );
        }

        return t(
          '1 license used. {{membersWithoutLicense}} users without a license selected ({{overage}} over capacity).',
          {
            membersWithoutLicense: selectedMembersWithoutLicense,
            overage: selectedMembersWithoutLicense - licensesAvailable
          }
        );
      }

      if (licensesUsed > 1 || licensesUsed === 0) {
        if (selectedMembersWithoutLicense === 1) {
          return t(
            '{{licensesUsed}} licenses used. 1 user without a license selected (1 over capacity).',
            {
              licensesUsed
            }
          );
        }

        return t(
          '{{licensesUsed}} licenses used. {{membersWithoutLicense}} users without a license selected ({{overage}} over capacity).',
          {
            licensesUsed,
            membersWithoutLicense: selectedMembersWithoutLicense,
            overage: selectedMembersWithoutLicense - licensesAvailable
          }
        );
      }
    }
  } else if (accessValue === 'remove') {
    const licensesMadeAvailable =
      selectedMembersWithLicense + licensesAvailable;
    if (licensesMadeAvailable === 1) {
      return t('1 license available.');
    }
    return t('{{licensesMadeAvailable}} licenses available.', {
      licensesMadeAvailable
    });
  }

  return t('0 licenses available.');
};

export const getProductOptions = (
  product: v2.Product,
  selectedMembersWithLicense: number,
  selectedMembersWithoutLicense: number,
  remainingLicenses: number,
  t: TFunction,
  featureFlags: FeatureFlag[] = []
): RadioItem[] => {
  const hasAccessLevelManagement = productHasAccessLevelManagement(
    product.slug as ProductSlugs,
    featureFlags
  );
  const isAuditor = product.slug === ProductSlugs.axeAuditor;
  const canGrantAccess =
    selectedMembersWithoutLicense > 0 && remainingLicenses >= 0;
  const shouldShowAdmin = hasAccessLevelManagement && canGrantAccess;
  const shouldShowView =
    isAuditor && hasAccessLevelManagement && canGrantAccess;
  const shouldShowGeneral = canGrantAccess;
  const shouldShowRemove = selectedMembersWithLicense > 0;
  const shouldShowNone =
    remainingLicenses >= 0 || selectedMembersWithLicense > 0;

  const options: RadioItem[] = [];

  if (shouldShowNone) {
    options.push({
      id: `${product.slug}-none`,
      value: 'none',
      label: t('No change')
    });
  }

  if (shouldShowGeneral) {
    options.push({
      id: `${product.slug}-general`,
      value: 'general',
      label: t('General access only')
    });
  }

  if (shouldShowView) {
    options.push({
      id: `${product.slug}-view`,
      value: 'view',
      label: t('View access only')
    });
  }

  if (shouldShowAdmin) {
    options.push({
      id: `${product.slug}-admin`,
      value: 'admin',
      label: t('Make administrator')
    });
  }

  if (shouldShowRemove) {
    options.push({
      id: `${product.slug}-remove`,
      value: 'remove',
      label: t('Remove access')
    });
  }

  return options;
};
